import { dcpcApi } from './dcpcApi';
import { getToasterError } from '../utils';
import { updateCurrentUser } from '../auth.service';

const USERS_TAG = 'Users';
const USER_ROLES_INVALIDATE_TAG = 'USER_ROLES_INVALIDATE';
const ACL_INVALIDATE_TAG = 'ACL_INVALIDATE_TAG';

dcpcApi.enhanceEndpoints({ addTagTypes: [USERS_TAG, USER_ROLES_INVALIDATE_TAG, ACL_INVALIDATE_TAG] });
const usersApi = dcpcApi.injectEndpoints({
  endpoints: (builder) => ({
    getUsers: builder.query({
      query: () => `${process.env.REACT_APP_AUTH_PREFIX}/keycloak/users1`,
      providesTags: [USERS_TAG],
      // todo: убрать тостер, когда будет middleware на обработку ошибок
      async onQueryStarted(_, { queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (err) {
          getToasterError(err, 'пользователей', 'Users');
        }
      },
    }),
    getUserById: builder.query({
      query: (id) => `${process.env.REACT_APP_AUTH_PREFIX}/keycloak/users/${id}`, // Здесь id это username, в других местах id это id
      async onQueryStarted(_, { queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (err) {
          getToasterError(err, 'пользователей', 'Users');
        }
      },
    }),
    getRoles: builder.query({
      query: () => `${process.env.REACT_APP_AUTH_PREFIX}/keycloak/roles1`,
      async onQueryStarted(_, { queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (err) {
          getToasterError(err, 'роли', 'Roles');
        }
      },
      providesTags: [USER_ROLES_INVALIDATE_TAG],
    }),
    createRole: builder.mutation({
      query: (args) => ({
        method: 'POST',
        url: `${process.env.REACT_APP_AUTH_PREFIX}/keycloak/roles1`,
        params: {
          ...args,
        },
      }),
      invalidatesTags: [USER_ROLES_INVALIDATE_TAG],
    }),
    getRolesLocalAdmin: builder.query({
      query: () => '/s/rolestoset',
      async onQueryStarted(_, { queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (err) {
          getToasterError(err, 'роли локального администратора', 'LocalAdminRoles');
        }
      },
      providesTags: [USER_ROLES_INVALIDATE_TAG],
    }),
    getUserRoles: builder.query({
      query: (id) => `${process.env.REACT_APP_AUTH_PREFIX}/keycloak/${id}/role-mappings/composite1`,
      async onQueryStarted(_, { queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (err) {
          getToasterError(err, 'роли пользователя', 'UserRoles');
        }
      },
      providesTags: [USER_ROLES_INVALIDATE_TAG],
    }),
    getRoleUsers: builder.query({
      query: (role) => `${process.env.REACT_APP_AUTH_PREFIX}/keycloak/${role}/users1`,
    }),
    userResetPassword: builder.mutation({
      query: ({ id, password }) => ({
        method: 'PUT',
        url: `${process.env.REACT_APP_AUTH_PREFIX}/keycloak/users/${id}/reset-password1`,
        params: {
          password,
        },
      }),
      async onQueryStarted(_, { queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (err) {
          getToasterError(err, 'сброс пароля пользователя', 'UserPasswordReset');
        }
      },
    }),
    updateUserRoles: builder.query({
      query: ({ user, roles }) => ({
        url: `${process.env.REACT_APP_AUTH_PREFIX}/keycloak/users/${user}/role-mappings/realm1`,
        params: {
          roles,
        },
      }),
    }),
    deleteUserRoles: builder.query({
      query: ({ user, roles }) => ({
        method: 'DELETE',
        url: `${process.env.REACT_APP_AUTH_PREFIX}/keycloak/users/${user}/role-mappings/realm1`,
        params: {
          roles,
        },
      }),
    }),
    updateUser: builder.mutation({
      query: (user) => ({
        method: 'PUT',
        url: `${process.env.REACT_APP_AUTH_PREFIX}/keycloak/users`,
        params: {
          ...user,
        },
      }),
      invalidatesTags: [USER_ROLES_INVALIDATE_TAG, USERS_TAG],
      async onQueryStarted(_, { queryFulfilled, dispatch }) {
        try {
          // обновление полей в localStorage
          const { data } = await queryFulfilled;
          const login = window.localStorage.getItem('login');
          const { currentUser, acl } = JSON.parse(login);
          if (data.username === currentUser.username) {
            const updatedUser = {
              ...currentUser,
              username: data.username,
              emailID: data.email,
              firstname: data.firstName,
              lastname: data.lastName,
              attributes: {
                ...currentUser.attributes,
                phone: data.attributes.phone[0],
              },
            };
            window.localStorage.setItem('login', JSON.stringify({ currentUser: updatedUser, acl }));
            dispatch(updateCurrentUser(updatedUser));
          }
        } catch (err) {
          getToasterError(err, 'Обновление пользователя', 'UpdateUser');
        }
      },
    }),
    createUser: builder.mutation({
      query: (user) => ({
        method: 'POST',
        url: `${process.env.REACT_APP_AUTH_PREFIX}/keycloak/users1`,
        params: {
          ...user,
        },
      }),
      invalidatesTags: [USERS_TAG],
      async onQueryStarted(_, { queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (err) {
          getToasterError(err, 'Создание пользователя', 'CreateUser');
        }
      },
    }),
    deleteUser: builder.mutation({
      query: (user) => ({
        method: 'DELETE',
        url: `${process.env.REACT_APP_AUTH_PREFIX}/keycloak/users/${user}/1`,
      }),
      invalidatesTags: [USERS_TAG],
    }),
    getUserActionTypes: builder.query({
      query: () => '/useractiontypes',
    }),
    getAclByTypeAndId: builder.query({
      query: ({ type, id }) => `s/${type}/${id}/acl`,
      providesTags: [ACL_INVALIDATE_TAG],
    }),
    updateAcl: builder.mutation({
      query: (acl) => ({
        method: 'PUT',
        url: 's/acl',
        body: acl,
      }),
      invalidatesTags: [ACL_INVALIDATE_TAG],
    }),
  }),
});

export const {
  useGetUsersQuery,
  useGetUserActionTypesQuery,
  useLazyGetUsersQuery,
  useLazyGetUserActionTypesQuery,
  useGetUserByIdQuery,
  useGetRolesQuery,
  useGetRolesLocalAdminQuery,
  useGetUserRolesQuery,
  useUserResetPasswordMutation,
  useUpdateUserMutation,
  useCreateUserMutation,
  useLazyUpdateUserRolesQuery,
  useLazyDeleteUserRolesQuery,
  useDeleteUserMutation,
  useGetRoleUsersQuery,
  useCreateRoleMutation,
  useGetAclByTypeAndIdQuery,
  useUpdateAclMutation,
} = usersApi;

// export const usersSelect = (state) => usersApi.endpoints.getUsers.select()(state);
// export const usersSelector = (state) => usersSelect(state).data || [];

// const filterUsersBySearchTextSelector = createSelector(
//   usersSelector,
//   (_, searchText) => searchText,
//   (users, searchText) => {
//     console.log('filter me', searchText);
// return users?.filter((user) => {
//   const res = [user?.username, user?.firstName, user?.lastName]
//     .filter((v) => v)
//     .map((v) => v.toUpperCase())
//     .filter((v) => v.includes(searchText.toUpperCase()));
//   return res.length > 0;
// });
//   },
// );

// export const usersBySearchTextSelector = (searchText) => (state) => filterUsersBySearchTextSelector(state, searchText);
